import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs/lib/anime.es.js";
import { Settings } from "../../../settings";
import TickerTimeout from "../../../utils/TickerTimeout";
import {
  FEATURES_CONFIG,
  WIN_TYPE_THRESHOLDS,
  ANIMATED_WIN_TYPES,
} from "../../../featuresConfig";
import { PixiSettings } from "../../../pixiSettings";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import {
  setWinAnimationsPlaying,
  setSymbolPopupBlocked,
  setWinAnimationCounterFinished,
} from "../../../State/actions";
import { syncGameState, updateDisplayedBalance } from "../../../State/Slices/gameSettingsSlice";
import { applyReplayWin } from "../../../State/Slices/replaySlice";
import store from "../../../State/Store/store";
import { formatToCustomLocale } from "../../../utilities";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";
import { HowlCache } from "../../../State/Slices/soundsSlice";
import { handleShouldSyncBalanceOnWinClose } from "../../../service";



// ============================================================================
// WIN ANIMATION CONFIGURATION
// Fine-tune spine scale, position offset, and text sizes per device/orientation
// ============================================================================

// Device multipliers — only spine scale and vertical offset per device
// Button position and font size are controlled by spine bones directly
const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = {
  scale: 1.0,
  offset: 0.0,
};

const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = {
  scale: 1.0,
  offset: 0.0,
};

const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = {
  scale: 1.0,
  offset: 0.0,
};

const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = {
  scale: 1.0,
  offset: 0.0,
};

const DEVICE_MULTIPLIERS_DESKTOP = {
  scale: 1.0,
  offset: 0.0,
};


/**
 * WinAnimationSecondaryController - Manages progressive win celebration animations (secondary theme)
 *
 * Simplified variant for Zeus/Dmon theme:
 * - Counter stays permanently attached to txt_slot_value slot (no detach/reattach)
 * - Tier transitions swap animations directly (no hide→reset→show dance)
 * - Skin switching ready (skeleton.setSkin) for future artist skins
 *
 * @class
 */
class WinAnimationSecondaryController {
  /**
   * Creates a new WinAnimationSecondaryController instance
   *
   * @param {PIXI.Container} container - Parent container for spine animations
   * @param {Object} dimensionsContext - Device dimensions and context
   * @param {Object} overlayRef - Reference to background overlay
   * @param {Function} dispatch - Redux dispatch function
   * @param {number} symbolWidth - Width of game symbols for scaling
   * @param {number} symbolHeight - Height of game symbols for scaling
   * @param {Function} t - Translation function from i18next
   */
  constructor(
    container,
    dimensionsContext,
    overlayRef,
    dispatch,
    symbolWidth,
    symbolHeight,
    t,
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.overlayRef = overlayRef;
    this.dispatch = dispatch;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.t = t;

    // Extract dimensions object for backward compatibility
    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.winSpine = null;
    this.isVisible = false;
    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.initialized = false;

    // Counter attached to spine bone
    this.counterContainer = null;
    this._charTexts = [];
    this.currentCounterValue = 0;
    this.counterAnimationRef = null;

    // Multiplier text attached to spine bone ("32 X BET")
    this.multiplierContainer = null;
    this.multiplierText = null;

    // Button attached to spine bone
    this.buttonContainer = null;
    this.buttonText = null;
    // Proxy set by the React shell — routes taps on the "TAP TO CONTINUE"
    // text through the same fullscreen+sound handling as the full-screen overlay.
    this._clickHandler = null;

    // Timeouts and cleanup
    this.currentTimeouts = [];
    this.emergencyRecoveryTimeout = null;
    this.autoCloseTimeout = null;
    this.skipProtectionTimeout = null;
    this.debounceTimeout = null;

    // Animation state
    this.animationQueue = [];
    this.currentAnimationIndex = 0;
    this.finalWinType = null;
    this.lastWinAmount = null; // Prevent restart for same win

    this.currentAnimationPhase = null;
    this.phaseStartTime = 0;
    this.phaseProgress = 0;

    // User interaction state
    this.hasHandledSkipClick = false;
    this.hasHandledCloseClick = false;
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;
    this.isClosing = false;
    this.isFinishing = false;
    this.counterJumpedToFinal = false;
    this.counterDetached = false; // Track whether counter is detached from spine slot
    this.detachedScaleX = 1; // Scale factor when counter is detached
    this.detachedScaleY = 1;

    // Background music state tracking
    this.backgroundMusicWasPlaying = false;
    this.backgroundMusicType = null; // Track which background music was playing (background_music or free_spin_background_music)

    // Internal caches for performance optimization
    this._deviceMultipliersCache = null;
    this._availableAnimationsCache = null;
    this._animationProgressionCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeWinSpine();
  }

  /**
   * Apply proportional scaling and centering to spine animation
   * Scales based on symbol size and device type, positions relative to screen center
   */
  applyScaleAndCenter() {
    if (!this.winSpine || !this.dimensions) return;

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Get device/orientation-specific multipliers
    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    this.winSpine.scale.set(scale);

    // Apply device/orientation-specific offset multiplier
    const baseOffset = this.symbolHeight || 0;

    // Position relative to centered parent container (0,0 is screen center)
    this.winSpine.x = 0;
    this.winSpine.y = -baseOffset * deviceOffsetMultiplier;

    // Update detached counter position to match new spine transform
    this.syncDetachedCounter();
  }

  /**
   * Get device-specific scale and offset multipliers for spine animations
   * Returns both scale and offset values from configuration constants
   * Cached for performance - invalidated when device/orientation changes
   */
  getDeviceMultipliers() {
    const { device, orientation } = this.dimensionsContext;

    // Check cache validity
    if (
      this._deviceMultipliersCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._deviceMultipliersCache;
    }

    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = DEVICE_MULTIPLIERS_MOBILE_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = DEVICE_MULTIPLIERS_TABLET_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS_TABLET_LANDSCAPE;
      }
    } else {
      multipliers = DEVICE_MULTIPLIERS_DESKTOP;
    }

    // Cache result
    this._deviceMultipliersCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return multipliers;
  }



  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   */
  updateDimensions(dimensionsContext, symbolWidth, symbolHeight) {
    // Check if any values actually changed to avoid unnecessary recalculations
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightsChanged =
      this.symbolWidth !== symbolWidth || this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    // Only proceed if something actually changed - this prevents unnecessary updates during animation start
    if (!dimensionsChanged && !symbolHeightsChanged) {
      return;
    }

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions; // Update the extracted dimensions object
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    // Invalidate caches when device/orientation changes
    if (
      deviceChanged ||
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation
    ) {
      this._deviceMultipliersCache = null;
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }


    // CRITICAL: Always update spine positioning whenever dimensions change, even when hidden
    // This ensures the spine is always positioned correctly, preventing jumps on first appearance
    // Similar to FreeSpinIntroDialogController which maintains positioning at all times
    if (
      this.winSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightsChanged)
    ) {
      // Ensure we have valid symbolHeight before positioning
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }

    // Update counter display if dimensions changed (font size needs adjustment based on spine scale)
    if (dimensionsChanged || symbolHeightsChanged) {
      if (this.counterContainer && this.currentCounterValue !== undefined) {
        // Font size is now based on spine scale, so update when spine is rescaled
        this.updateCounterDisplay(this.currentCounterValue);
      }
    }
  }

  /**
   * Clear all tracked timeouts and timers
   *
   * Clears animation timeouts, auto-close timers, skip protection timers,
   * and emergency recovery timers. Prevents memory leaks and ensures
   * clean state when animations are interrupted.
   */
  clearAllTimeouts() {
    for (const t of this.currentTimeouts) {
      if (t && typeof t.clear === "function") {
        t.clear();
      }
    }
    this.currentTimeouts = [];

    // Clear skip protection timeout
    if (this.skipProtectionTimeout) {
      this.skipProtectionTimeout.clear();
      this.skipProtectionTimeout = null;
    }

    // Clear emergency recovery timeout
    if (this.emergencyRecoveryTimeout) {
      this.emergencyRecoveryTimeout.clear();
      this.emergencyRecoveryTimeout = null;
    }

    // Clear auto-close timeout
    if (this.autoCloseTimeout) {
      this.autoCloseTimeout.clear();
      this.autoCloseTimeout = null;
    }

    // Clear debounce timeout
    if (this.debounceTimeout) {
      this.debounceTimeout.clear();
      this.debounceTimeout = null;
    }
  }

  /**
   * Clear all resources (just timeouts now, anime fades handle themselves)
   */
  clearAllResources() {
    this.clearAllTimeouts();
  }

  /**
   * Initialize the win celebration spine
   */
  initializeWinSpine() {
    try {
      this.winSpine = this.createWinSpine();

      if (!this.winSpine) {
        return;
      }

      // Simple validation
      if (!this.winSpine.skeleton || !this.winSpine.state) {
        return;
      }

      // Add to container first
      this.container.addChild(this.winSpine);

      // Set initial properties
      this.winSpine.alpha = 0.001; // Hidden initially (use alpha not visible)

      // Apply initial position and scale (only if dimensions are ready)
      // If dimensions aren't ready yet, updateDimensions will position it when they become available
      if (this.symbolHeight && this.symbolHeight > 0 && this.dimensions) {
        this.applyScaleAndCenter();
      }

      this.initialized = true;

      // Configure animation mixing (crossfade) for smooth transitions
      // Spine interpolates bone positions between animations over the mix duration
      const stateData = this.winSpine.state.data;
      stateData.setMix("idle", "out", 0.25);  // 250ms blend: IDLE → OUT (closing)
      stateData.setMix("in", "idle", 0.15);   // 150ms blend: IN → IDLE (settling)
      stateData.setMix("idle", "in", 0.2);    // 200ms blend: IDLE → IN (tier transition BW→MW→SMW)
      stateData.setMix("out", "in", 0.2);     // 200ms blend: OUT → IN (skip to final tier)

      // Invalidate animations cache when spine is reinitialized
      this._availableAnimationsCache = null;

      // Initialize spine structure to compile shaders during loading
      this.initializeSpineStructure();

      // Force-upload all win_celebration atlas page textures to GPU
      SpineAtlasUploader.upload("win_celebration");

      // Initialize counter attached to spine bone
      this.initializeCounter();

      // Initialize multiplier text attached to spine bone
      this.initializeMultiplier();

      // Initialize button attached to spine bone
      this.initializeButton();
    } catch (error) {
      // Silent fail - spine initialization failed
    }
  }

  /**
   * Create win spine from loaded assets
   */
  createWinSpine() {
    try {
      // Check if assets are loaded
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "win_celebration-skeleton";
      const atlasAlias = "win_celebration-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const winSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!winSpine || !winSpine.skeleton || !winSpine.state) {
        return null;
      }

      if (!winSpine.skeleton.data || !winSpine.skeleton.data.animations) {
        return null;
      }

      return winSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   * Minimal requirement: set one animation + update once
   */
  initializeSpineStructure() {
    if (!this.winSpine || !this.winSpine.state) {
      return;
    }

    try {
      // Set any animation to force spine to load bones/slots/attachments
      // Just pick the first available animation (usually big_win_in)
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.winSpine.state.setAnimation(0, animations[0], false);
        this.winSpine.state.update(0.016); // Update one frame
        this.winSpine.skeleton.updateWorldTransform(); // Populate hierarchy
      }

      // Keep at alpha = 0.001 so PixiJS continues rendering it
      // This keeps GPU buffers hot after skeleton is populated
      this.winSpine.alpha = 0.001;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Get available animations from the spine
   * Cached for performance - invalidated when spine is reinitialized
   */
  getAvailableAnimations() {
    if (!this.winSpine?.skeleton?.data?.animations) return [];

    // Check cache validity
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    // Calculate and cache
    const animations = this.winSpine.skeleton.data.animations.map(
      (anim) => anim.name,
    );
    this._availableAnimationsCache = animations;

    return animations;
  }

  /**
   * Initialize counter and attach to spine slot "txt_slot_value"
   *
   * The counter is attached to a spine slot, so Spine automatically handles
   * position, rotation, and scale updates. No manual transform updates needed.
   * The counter only manages its own text content and visibility.
   */
  initializeCounter() {
    if (!this.winSpine || !this.winSpine.skeleton) {
      return;
    }

    // Check if slot exists in spine
    const counterSlot = this.winSpine.skeleton.findSlot("txt_slot_value");
    if (!counterSlot) {
      return;
    }

    // Create counter container and attach to slot
    this.counterContainer = new PIXI.Container();
    this.counterContainer.name = "win_counter_attached_to_slot";
    this.counterContainer.alpha = 0.001;

    // Use Zeus-themed counter style — pixel properties scale proportionally with fontSize
    const counterBaseStyle = FONT_STYLES.winCelebrationCounter;
    this._counterBaseStyle = counterBaseStyle;
    this._counterStyle = scaleStyle(counterBaseStyle);

    // Measure per-character advance width accurately:
    // "00".width / 2 gives the true advance width (shared padding cancels out)
    const measureStyle = new PIXI.TextStyle({
      fontFamily: this._counterStyle.fontFamily,
      fontSize: this._counterStyle.fontSize,
      fontWeight: this._counterStyle.fontWeight || "normal",
    });
    const CHAR_PADDING = 4; // px breathing room between characters
    const doubleProbe = new PIXI.Text("00", measureStyle);
    this._digitCellWidth = (doubleProbe.width / 2) + CHAR_PADDING;
    doubleProbe.destroy();
    const sepProbe = new PIXI.Text(",,", measureStyle);
    this._separatorCellWidth = (sepProbe.width / 2) + CHAR_PADDING;
    sepProbe.destroy();

    // Per-character text objects will be created dynamically
    this._charTexts = [];

    // Attach to slot — Spine auto-updates position/rotation/scale
    this.winSpine.addSlotObject("txt_slot_value", this.counterContainer);

    // Cache the bone for overflow checks
    const bone = counterSlot.bone;
    this._counterBoneWidth = bone ? bone.data.length || 400 : 400;

    // Initialize with zero
    this.updateCounterDisplay(0);
  }

  /**
   * Initialize multiplier text and attach to spine's "txt_slot_multiplier" slot.
   * Shows "XX X BET" below the counter — same font, slightly smaller.
   */
  initializeMultiplier() {
    if (!this.winSpine || !this.winSpine.skeleton) return;

    const multiplierSlot = this.winSpine.skeleton.findSlot("txt_slot_multiplier");
    if (!multiplierSlot) return;

    this.multiplierContainer = new PIXI.Container();
    this.multiplierContainer.name = "win_multiplier_attached_to_slot";

    // Same font as counter but 60% size
    const multiplierFontSize = Math.round(this._counterBaseStyle.fontSize * 0.6);
    const multiplierStyle = scaleStyle(this._counterBaseStyle, multiplierFontSize);

    this.multiplierText = new PIXI.Text("", new PIXI.TextStyle(multiplierStyle));
    this.multiplierText.anchor.set(0.5);
    this.multiplierText.alpha = 0;
    this.multiplierText.visible = false;

    this.multiplierContainer.addChild(this.multiplierText);
    this.winSpine.addSlotObject("txt_slot_multiplier", this.multiplierContainer);

    // Cache bone width for overflow
    const bone = multiplierSlot.bone;
    this._multiplierBoneWidth = bone ? bone.data.length || 400 : 400;

    // Multiplier stays hidden until counter finishes — no counting animation
  }

  /**
   * Update multiplier display with a value.
   * Shows "XX X BET" where XX = floor(value / betAmount).
   *
   * @param {number} value - The value to display
   */
  updateMultiplierDisplay(value) {
    if (!this.multiplierText || !this.betAmount || this.betAmount <= 0) return;

    const multiplier = Math.floor(value / this.betAmount);
    this.multiplierText.text = `${multiplier} X BET`;

    // Multiplier always lives in slot — only use bone-local overflow
    this.multiplierContainer.scale.set(1);
    const maxWidth = this._multiplierBoneWidth * 0.9;
    if (this.multiplierText.width > maxWidth && maxWidth > 0) {
      this.multiplierContainer.scale.set(maxWidth / this.multiplierText.width);
    }
  }

  /**
   * Update counter display with new value.
   * Uses per-character fixed-width cells so digits don't cause horizontal jitter.
   * Each character gets its own PIXI.Text centered in a cell of equal width.
   *
   * @param {number} value - The value to display
   */
  updateCounterDisplay(value) {
    if (!this.counterContainer) return;

    const formattedText = formatToCustomLocale(Math.floor(value));
    const chars = formattedText.split("");

    // Calculate total width using fixed cell widths
    let totalWidth = 0;
    const cellWidths = chars.map((ch) => {
      const isDigit = ch >= "0" && ch <= "9";
      const w = isDigit ? this._digitCellWidth : this._separatorCellWidth;
      totalWidth += w;
      return w;
    });

    // Create/reuse PIXI.Text objects for each character
    while (this._charTexts.length < chars.length) {
      const t = new PIXI.Text("", new PIXI.TextStyle(scaleStyle(this._counterBaseStyle)));
      t.anchor.set(0.5);
      this.counterContainer.addChild(t);
      this._charTexts.push(t);
    }
    // Hide excess
    for (let i = chars.length; i < this._charTexts.length; i++) {
      this._charTexts[i].visible = false;
    }

    // Position each character in its fixed-width cell, centered around x=0
    let x = -totalWidth / 2;
    for (let i = 0; i < chars.length; i++) {
      const t = this._charTexts[i];
      t.visible = true;
      t.text = chars[i];
      t.x = x + cellWidths[i] / 2;
      t.y = 0;
      x += cellWidths[i];
    }

    // Auto-scale counter to fit — matches reference WinAnimationController pattern
    if (this.counterDetached) {
      // When detached, use SCREEN WIDTH for overflow (same as reference controller)
      // detachedScale maps local→screen, so convert maxWidth to local space
      const maxWidth = (this.dimensions?.width || 800) * 0.85;
      const localMaxWidth =
        this.detachedScaleX > 0 ? maxWidth / this.detachedScaleX : maxWidth;
      if (totalWidth > localMaxWidth && localMaxWidth > 0) {
        const scale = localMaxWidth / totalWidth;
        this.counterContainer.scale.set(
          scale * this.detachedScaleX,
          scale * this.detachedScaleY,
        );
      } else {
        this.counterContainer.scale.set(
          this.detachedScaleX,
          this.detachedScaleY,
        );
      }
    } else {
      this.counterContainer.scale.set(1);
      const maxWidth = this._counterBoneWidth * 0.9;
      if (totalWidth > maxWidth && maxWidth > 0) {
        this.counterContainer.scale.set(maxWidth / totalWidth);
      }
    }
  }

  /**
   * Show counter (visibility only, spine controls position/scale/rotation)
   */
  showCounter() {
    if (!this.counterContainer) return;
    this.counterContainer.alpha = 1;
    // Multiplier stays hidden until counter finishes (showMultiplierAndButton)
  }

  /**
   * Hide counter (visibility only, spine controls position/scale/rotation)
   */
  hideCounter() {
    if (!this.counterContainer) return;
    this.counterContainer.alpha = 0.001;
    if (this.multiplierContainer) this.multiplierContainer.alpha = 0;
  }

  /**
   * Detach counter and multiplier from spine slots — they stay at fixed position,
   * unaffected by bone transforms (IN/OUT scale animations).
   * Used between tier transitions to keep counter visible.
   */
  detachCounterFromSlot() {
    if (!this.counterContainer || this.counterDetached || !this.winSpine) {
      return;
    }

    // Find the bone that drives the txt_slot_value slot
    const bone = this.winSpine.skeleton.findBone("value_placeholder");
    if (!bone) {
      return;
    }

    // Ensure bone world transforms are current
    this.winSpine.skeleton.updateWorldTransform(2);

    // Calculate position: bone world coords → global → parent-local
    const globalPos = this.winSpine.toGlobal({
      x: bone.worldX,
      y: bone.worldY,
    });
    const localPos = this.container.toLocal(globalPos);

    // Capture spine pixi scale and bone scale at detach time
    // These are frozen so tier OUT/IN animations don’t corrupt the counter
    const spineScaleX = Math.abs(this.winSpine.scale.x);
    const spineScaleY = Math.abs(this.winSpine.scale.y);
    const boneScaleX = Math.sqrt(bone.a * bone.a + bone.b * bone.b);
    const boneScaleY = Math.sqrt(bone.c * bone.c + bone.d * bone.d);

    // Save for sync on resize
    this._detachSpineScaleX = spineScaleX;
    this._detachSpineScaleY = spineScaleY;
    this._detachBoneScaleX = boneScaleX;
    this._detachBoneScaleY = boneScaleY;


    // Remove from spine slots
    this.winSpine.removeSlotObject("txt_slot_value", this.counterContainer);

    // Null out slot attachments so spine doesn’t render baked-in visuals
    const valueSlot = this.winSpine.skeleton.findSlot("txt_slot_value");
    if (valueSlot) valueSlot.setAttachment(null);

    // Add to parent container (completely outside spine’s hierarchy)
    this.container.addChild(this.counterContainer);

    // Apply position and scale — matches reference WinAnimationController exactly
    this.counterContainer.x = localPos.x;
    this.counterContainer.y = localPos.y;
    this.detachedScaleX = spineScaleX * boneScaleX;
    this.detachedScaleY = spineScaleY * boneScaleY;
    this.counterContainer.scale.set(this.detachedScaleX, this.detachedScaleY);
    this.counterContainer.rotation = 0;

    this.counterDetached = true;
  }

  /**
   * Sync detached counter/multiplier position/scale with spine’s current transform.
   * Called from applyScaleAndCenter on resize to keep counter aligned.
   *
   * Uses frozen bone scale from detach time, NOT the current animated bone transforms.
   * Only the spine’s pixi scale (stable device-based value) is re-read.
   */
  syncDetachedCounter() {
    if (!this.counterDetached || !this.counterContainer || !this.winSpine)
      return;

    // Recalculate position from spine’s current pixi transform
    const bone = this.winSpine.skeleton.findBone("value_placeholder");
    if (!bone) return;

    this.winSpine.skeleton.updateWorldTransform(2);

    const globalPos = this.winSpine.toGlobal({
      x: bone.worldX,
      y: bone.worldY,
    });
    const localPos = this.container.toLocal(globalPos);

    this.counterContainer.x = localPos.x;
    this.counterContainer.y = localPos.y;

    // Scale: use FROZEN bone scale from detach time, only adjust for spine pixi scale change
    const currentSpineScaleX = Math.abs(this.winSpine.scale.x);
    const currentSpineScaleY = Math.abs(this.winSpine.scale.y);

    this.detachedScaleX = currentSpineScaleX * this._detachBoneScaleX;
    this.detachedScaleY = currentSpineScaleY * this._detachBoneScaleY;
    this.counterContainer.scale.set(this.detachedScaleX, this.detachedScaleY);


    this.updateCounterDisplay(this.currentCounterValue);
  }

  /**
   * Reattach counter and multiplier to spine slots — they follow bone transforms again.
   * Used before closing OUT animation so counter scales out with the bone.
   */
  reattachCounterToSlot() {
    if (!this.counterContainer || !this.counterDetached || !this.winSpine)
      return;

    // Remove from parent container
    if (this.counterContainer.parent) {
      this.counterContainer.parent.removeChild(this.counterContainer);
    }

    // Re-attach to spine slot
    this.winSpine.addSlotObject("txt_slot_value", this.counterContainer);

    // Reset scale to slot-local
    this.counterContainer.scale.set(1);
    this.counterContainer.x = 0;
    this.counterContainer.y = 0;
    this.counterContainer.rotation = 0;

    // Multiplier stays in its slot permanently — no reattach needed

    this.counterDetached = false;
    this.detachedScaleX = 1;
    this.detachedScaleY = 1;

    // Re-render counter at slot-local scale
    this.updateCounterDisplay(this.currentCounterValue);
  }



  /**
   * Registers the React shell's dismiss handler (fullscreen + sound + click
   * accept), so the "TAP TO CONTINUE" text can trigger the same flow as the
   * full-screen overlay when tapped directly.
   */
  setClickHandler(handler) {
    this._clickHandler = handler;
  }

  /**
   * Initialize button text and attach to spine slot with alpha 0.
   *
   * Per GDD: "Tap to Continue" only appears AFTER the win counter has finished
   * counting. Attached to slot on init (same as counter), starts invisible,
   * and the spine's OUT animation naturally carries it away — no manual hiding.
   */
  initializeButton() {
    if (!this.winSpine || !this.winSpine.skeleton) {
      return;
    }

    // Check if slot exists in spine
    const tapSlot = this.winSpine.skeleton.findSlot("txt_slot_tap_to_continue");
    if (!tapSlot) {
      return;
    }

    // Create container and attach to slot immediately (like the counter)
    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "win_continue_attached_to_slot";

    // Always "TAP TO CONTINUE" regardless of device
    const buttonLabel = this.t("tap_to_continue").toUpperCase();

    // Use intro_tap_to_start style — pixel properties scale proportionally with fontSize
    const style = FONT_STYLES.introTapToStart;
    const btnFontSize = 42;

    this.buttonText = new PIXI.Text(
      buttonLabel,
      new PIXI.TextStyle(scaleStyle(style, btnFontSize)),
    );
    this.buttonText.anchor.set(0.5);
    this.buttonText.alpha = 0;
    this.buttonText.visible = false;

    this.buttonContainer.addChild(this.buttonText);

    // Own hit area — hand cursor only shows over this text, not the whole
    // background overlay (matches the other popups' click-affordance pattern).
    this.buttonContainer.eventMode = "static";
    this.buttonContainer.cursor = "pointer";
    this.buttonContainer.on("pointertap", (e) => {
      if (e?.stopPropagation) e.stopPropagation();
      if (this._clickHandler) this._clickHandler(e);
    });

    // Scale text to fit within the bone's width
    const bone = tapSlot.bone;
    this._tapBoneWidth = bone ? bone.data.length || 400 : 400;
    if (this.buttonText.width > this._tapBoneWidth && this._tapBoneWidth > 0) {
      const scale = this._tapBoneWidth / this.buttonText.width;
      this.buttonContainer.scale.set(scale);
    }

    // Attach to slot — Spine auto-updates position/rotation/scale
    this.winSpine.addSlotObject("txt_slot_tap_to_continue", this.buttonContainer);
  }

  /**
   * Show multiplier ("XX X BET") with fade-in, then conditionally show "TAP TO CONTINUE".
   * Called when counter finishes counting on the final segment.
   *
   * Per GDD §17.2-17.3:
   * - BW/MW/SMW: show multiplier only, NO CTA button. Auto-close after 3s.
   * - Max Win: show multiplier AND "Tap to Continue" button.
   */
  showMultiplierAndButton() {
    // 200ms after counter finishes → fade in X BET
    const multTimeout = new TickerTimeout(() => {
      if (!this.isPlaying) return;

      if (this.multiplierText && this.betAmount > 0) {
        this.updateMultiplierDisplay(this.totalWinAmount);
        this.multiplierText.visible = true;
        this.multiplierText.alpha = 0;
        anime({
          targets: this.multiplierText,
          alpha: 1,
          duration: 400,
          easing: 'easeOutCubic',
        });
      }

      // GDD §17.2: CTA button ONLY on Max Win — BW/MW/SMW do NOT have a CTA button
      if (this.finalWinType === "max_win") {
        // 200ms after X BET appears → fade in TAP TO CONTINUE
        const btnTimeout = new TickerTimeout(() => {
          if (this.isPlaying && this.buttonText) {
            this.buttonText.visible = true;
            anime({
              targets: this.buttonText,
              alpha: 1,
              duration: 400,
              easing: 'easeOutCubic',
            });
          }
        }, 200);
        this.currentTimeouts.push(btnTimeout);
      }
    }, 200);
    this.currentTimeouts.push(multTimeout);
  }

  /**
   * Show only the multiplier ("XX X BET") without the CTA button.
   * Used for BW/MW/SMW per GDD §17.2-17.3.
   */
  showMultiplier() {
    if (!this.isPlaying) return;

    if (this.multiplierText && this.betAmount > 0) {
      this.updateMultiplierDisplay(this.totalWinAmount);
      this.multiplierText.visible = true;
      this.multiplierText.alpha = 0;
      anime({
        targets: this.multiplierText,
        alpha: 1,
        duration: 200,
        easing: 'easeOutCubic',
      });
    }
  }

  /**
   * Legacy alias — routes to showMultiplierAndButton.
   */
  showTapToContinue() {
    this.showMultiplierAndButton();
  }

  /**
   * Start counter segment for the current tier.
   * Each tier counts a proportional portion of the total win during its IDLE phase.
   * This ensures the counter only advances while visible — no off-screen counting.
   *
   * @param {boolean} isFinalSegment - If true, triggers completion callback when done
   */
  startTierCounter(isFinalSegment) {
    if (!this.counterSegmentTargets || this.currentSegmentIndex >= this.counterSegmentTargets.length) {
      return;
    }

    const segmentTarget = this.counterSegmentTargets[this.currentSegmentIndex];
    const segmentFrom = this.currentCounterValue || 0;
    // Counter runs for the full tier duration (IN + IDLE from spine data)
    const segmentDuration = this.getTierDuration();

    this.currentSegmentIndex++;

    const onComplete = isFinalSegment
      ? () => {
          if (!this.isMaxWinFinal) {
            if (this.dispatch) {
              this.dispatch(setWinAnimationCounterFinished(true));
            }
            // GDD §17.2: Show multiplier when counter finishes (single source of truth)
            this.showMultiplier();
            // GDD §17.3: Auto-close after 3s for BW/MW/SMW
            this.startAutoCloseTimer();
          }
        }
      : null;

    // If counter has already reached the target (e.g. win at exact threshold),
    // fire completion immediately — no point animating from 1000→1000
    if (segmentFrom >= segmentTarget) {
      if (onComplete) onComplete();
      return;
    }

    if (this.totalWinAmount > 0) {
      const easing = isFinalSegment ? "easeOutQuad" : "linear";

      this.startCounterAnimation(segmentFrom, segmentTarget, segmentDuration, onComplete, easing);
    }
  }

  /**
   * Animate counter value from one number to another
   *
   * @param {number} fromValue - Starting value
   * @param {number} toValue - Target value
   * @param {number} duration - Animation duration in ms
   * @param {Function} onComplete - Callback when animation completes
   * @param {string} easing - Anime.js easing function (default: easeOutQuad)
   */
  startCounterAnimation(fromValue, toValue, duration, onComplete, easing = "easeOutQuad") {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = fromValue;
    this.updateCounterDisplay(fromValue);

    // Start counter sound
    soundManager.play(SOUND_NAME.counter);

    this.counterAnimationRef = anime({
      targets: this,
      currentCounterValue: toValue,
      duration: duration,
      easing: easing,
      update: () => {
        this.updateCounterDisplay(this.currentCounterValue);
      },
      complete: () => {
        this.currentCounterValue = toValue;
        this.updateCounterDisplay(toValue);
        this.counterAnimationRef = null;


        // Stop counter sound and play end sound
        soundManager.stop(SOUND_NAME.counter);
        soundManager.play(SOUND_NAME.counter_end);

        // Call completion callback
        if (onComplete && typeof onComplete === "function") {
          onComplete();
        }
      },
    });
  }

  /**
   * Instantly set counter to specific value (skips animation)
   *
   * @param {number} value - Value to display
   */
  jumpCounterToValue(value) {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = value;
    this.updateCounterDisplay(value);
    soundManager.stop(SOUND_NAME.counter);
    soundManager.play(SOUND_NAME.counter_end);
  }

  /**
   * Reset counter to zero
   */
  resetCounter() {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = 0;
    this.updateCounterDisplay(0);
    soundManager.stop(SOUND_NAME.counter);
    soundManager.play(SOUND_NAME.counter_end);
  }

  /**
   * Check if counter value is currently animating
   *
   * @returns {boolean} True if counter is animating
   */
  isCounterAnimating() {
    return this.counterAnimationRef !== null;
  }

  /**
   * Get current counter value
   *
   * @returns {number} Current counter value
   */
  getCurrentCounterValue() {
    return this.currentCounterValue || 0;
  }

  /**
   * Calculate which win animations should play based on win multiplier
   *
   * Determines progressive win sequence based on win amount relative to bet:
   * - 20x+ bet = BIG_WIN animation
   * - 40x+ bet = BIG_WIN + MEGA_WIN animations
   * - 60x+ bet = BIG_WIN + MEGA_WIN + SUPER_MEGA_WIN animations
   *
   * @param {number} winAmount - Total win amount in credits
   * @param {number} betAmount - Bet amount for multiplier calculation
   * @returns {string[]} Array of win types in sequence (e.g., ['big_win', 'mega_win'])
   */
  calculateAnimationSequence(winAmount, betAmount) {
    if (!winAmount || !betAmount || winAmount <= 0 || betAmount <= 0) {
      return [];
    }

    // Read pre-computed winType from Redux state (set by RGS adapter)
    const state = store.getState();
    const finalWinType = state.game.currentRound?.winType || "";

    // If it's not an animated win type, return empty array
    if (!finalWinType || !ANIMATED_WIN_TYPES.includes(finalWinType)) {
      return [];
    }

    // Build sequence dynamically from provider's animated win types
    // Order win types by their thresholds (ascending)
    const winTypeOrder = ANIMATED_WIN_TYPES.map((winType) => ({
      type: winType,
      threshold: WIN_TYPE_THRESHOLDS[winType] || 0,
    }))
      .sort((a, b) => a.threshold - b.threshold)
      .map((item) => item.type);

    const sequence = [];
    const multiplier = winAmount / betAmount;

    // Add all win types that the multiplier qualifies for
    for (const winType of winTypeOrder) {
      // Skip max_win if feature is disabled
      if (winType === "max_win" && !FEATURES_CONFIG.MAX_WIN.ENABLED) continue;

      if (this.shouldPlayWinType(winType, multiplier)) {
        sequence.push(winType);
      }

      // Stop when we reach the final win type
      if (winType === finalWinType) {
        break;
      }
    }

    return sequence;
  }

  /**
   * Check if a win type should be played based on multiplier
   */
  shouldPlayWinType(winType, multiplier) {
    const threshold = WIN_TYPE_THRESHOLDS[winType] || 0;
    return threshold > 0 && multiplier >= threshold;
  }

  /**
   * Play sound matching the current win animation phase.
   * Sound names follow the pattern: {winType}_{phase} (e.g. big_win_in, big_win_idle, big_win_out)
   * Falls back to just {winType} if no phase-specific sound exists.
   */
  playWinPhaseSound = (winType, phase) => {
    if (!this.dispatch) return;

    // Try phase-specific sound first (e.g. big_win_in), fall back to generic (e.g. big_win)
    const phaseSoundKey = `${winType}_${phase}`;
    const soundKey = SOUND_NAME[phaseSoundKey] ? phaseSoundKey : winType;
    if (!SOUND_NAME[soundKey]) return;

    try {
      soundManager.play(SOUND_NAME[soundKey]);
    } catch (error) {
      // Silent fail
    }
  };

  /**
   * Fade in win music on loop for entire sequence (replaces individual win sounds)
   */
  playMainWinSound = () => {
    try {
      // Delay win_music fade-in to let pauseBackgroundMusic() fade-out finish (500ms)
      const fadeDelay = new TickerTimeout(() => {
        const winMusic = HowlCache[SOUND_NAME.win_loop];
        if (winMusic) {
          winMusic.loop(true); // Ensure it loops
          soundManager.fadeIn(SOUND_NAME.win_loop, 500);
        }
      }, 500);
      this.currentTimeouts.push(fadeDelay);
    } catch (error) {
      // Silent fail
    }
  };

  /**
   * Fade out background music if it's playing (smooth transition to win music)
   */
  pauseBackgroundMusic() {
    try {
      // Check both background music options and fade out whichever is playing
      const backgroundMusic = HowlCache[SOUND_NAME.background_music];
      const freeSpinBackgroundMusic =
        HowlCache[SOUND_NAME.free_spin_background_music];

      if (backgroundMusic && backgroundMusic.playing()) {
        this.backgroundMusicWasPlaying = true;
        this.backgroundMusicType = SOUND_NAME.background_music;
        soundManager.fadeOut(SOUND_NAME.background_music, 500);
      } else if (freeSpinBackgroundMusic && freeSpinBackgroundMusic.playing()) {
        this.backgroundMusicWasPlaying = true;
        this.backgroundMusicType = SOUND_NAME.free_spin_background_music;
        soundManager.fadeOut(SOUND_NAME.free_spin_background_music, 500);
      } else {
        this.backgroundMusicWasPlaying = false;
        this.backgroundMusicType = null;
      }
    } catch (error) {
      // Silent fail
      this.backgroundMusicWasPlaying = false;
      this.backgroundMusicType = null;
    }
  }

  /**
   * Fade in background music if it was playing before (smooth transition from win music)
   */
  resumeBackgroundMusic() {
    try {
      if (this.backgroundMusicWasPlaying && this.backgroundMusicType) {
        soundManager.fadeIn(this.backgroundMusicType, 500);
      }
      this.backgroundMusicWasPlaying = false; // Reset flag
      this.backgroundMusicType = null;
    } catch (error) {
      // Silent fail
      this.backgroundMusicWasPlaying = false;
      this.backgroundMusicType = null;
    }
  }

  /**
   * Stop banner sounds (win sounds specific to each animation)
   * Uses the win sound directly (no banner versions exist)
   */
  stopBannerSounds() {
    if (!this.dispatch) return;

    try {
      // Stop win sounds used for animations (no banner versions exist)
      const bannerSounds = [
        SOUND_NAME.big_win,
        SOUND_NAME.mega_win,
        SOUND_NAME.super_win,
        SOUND_NAME.epic_win,
        SOUND_NAME.max_win_in,
      ];

      bannerSounds.forEach((soundName) => {
        if (soundName) {
          soundManager.stop(soundName);
        }
      });
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Stop all win sounds (banner + main)
   */
  stopAllWinSounds() {
    this.stopBannerSounds();
    this.stopWinSounds();
    // Also stop max_win and related sounds if playing (for emergency cleanup)
    try {
      if (SOUND_NAME.max_win_loop) {
        soundManager.stop(SOUND_NAME.max_win_loop);
      }
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Fade out main win music (replaces stopping individual win sounds)
   */
  stopWinSounds() {
    if (!this.dispatch) return;

    try {
      // Fade out win_music instead of stopping it abruptly
      if (SOUND_NAME.win_loop) {
        soundManager.fadeOut(SOUND_NAME.win_loop, 500);
      }
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Start the progressive win animation sequence
   *
   * Determines which win animations to play based on win amount and bet size,
   * then plays them in sequence (BIG → SUPER → MEGA → EPIC).
   * Each animation follows: IN → IDLE → OUT pattern.
   * Counter animation runs in sync with spine animations.
   *
   * @param {number} winAmount - Total win amount in credits
   * @param {number} betAmount - Bet amount for win type threshold calculation
   * @returns {Promise} Resolves when animation sequence is complete
   */
  startSequence(winAmount, betAmount) {
    return new Promise((resolve, reject) => {
      // Prevent retriggering during finishing phase and duplicate wins
      if (
        this.isPlaying ||
        this.isFinishing ||
        winAmount === this.lastWinAmount ||
        !this.winSpine ||
        !this.winSpine.state
      ) {
        resolve();
        return;
      }

      // Calculate which animations to play
      this.animationQueue = this.calculateAnimationSequence(
        winAmount,
        betAmount,
      );

      if (this.animationQueue.length === 0) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.currentAnimationIndex = 0; // Start at 0 for first animation
      this.finalWinType = this.animationQueue[this.animationQueue.length - 1];
      this.totalWinAmount = winAmount;
      this.betAmount = betAmount;
      this.lastWinAmount = winAmount; // Track this win amount to prevent restart
      this.counterJumpedToFinal = false; // Reset counter jump flag

      // Reset user interaction guards at the beginning of a sequence
      this.hasHandledSkipClick = false;
      this.hasHandledCloseClick = false;
      this.isHandlingUserClick = false;
      this.isWaitingForClick = false; // Reset waiting state for new sequence
      this.isClosing = false; // Reset closing state for new sequence

      // SKIP PROTECTION: Block skipping for the first second
      this.canSkipAnimation = false;
      if (this.skipProtectionTimeout) {
        this.skipProtectionTimeout.clear();
      }
      this.skipProtectionTimeout = new TickerTimeout(() => {
        // Safety check: only enable skip if animation is still playing and not finished
        if (
          this.isPlaying &&
          !(this.hasHandledSkipClick && this.hasHandledCloseClick)
        ) {
          this.canSkipAnimation = true;
        }
      }, 300); // 300ms protection — prevents spin-button bleed-through

      // Enable scaling calculations when animation becomes active
      this.shouldBeActive = true;

      // Pause background music before starting win animation
      this.pauseBackgroundMusic();

      // Spine is already positioned correctly at all times (via updateDimensions)
      // Just ensure final positioning check before showing (spine is always warmed up and positioned)
      if (
        this.winSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      // Show and start animation - positioning is already correct since spine is always maintained
      this.showAndStartAnimation(resolve, reject);
    });
  }

  /**
   * Show spine, counter, overlay and start animation (extracted for reuse)
   */
  showAndStartAnimation(resolve, reject) {
    // Ensure "Tap to Continue" and multiplier start hidden for this new sequence
    if (this.buttonText) {
      this.buttonText.visible = false;
      this.buttonText.alpha = 0;
    }
    if (this.multiplierText) {
      this.multiplierText.visible = false;
      this.multiplierText.alpha = 0;
    }
    // Fade in spine smoothly (synchronized with counter/overlay)
    if (this.winSpine) {
      anime({
        targets: this.winSpine,
        alpha: [0.001, 1],
        duration: 350, // Match counter fade-in duration
        easing: "easeOutQuad",
      });
    }

    this.isVisible = true;
    this.currentAnimationPhase = "spine-animation";
    this.phaseStartTime = performance.now();
    this.phaseProgress = 0;

    // Initialize counter
    this.resetCounter();
    this.showCounter();


    // Show background overlay (synchronized)
    this.overlayRef.current?.fadeInBackground();

    // Start animation after positioning is confirmed
    this.startFirstAnimation().then(resolve).catch(reject);
  }



  /**
   * Play the next animation in the queue.
   * Detaches counter from slot (bone forced to rest scale 1.0) so it stays
   * frozen while the new tier's IN animation plays behind it.
   */
  playNextAnimation() {
    return new Promise((resolve, reject) => {
      if (this.currentAnimationIndex >= this.animationQueue.length) {
        resolve();
        return;
      }

      const winType = this.animationQueue[this.currentAnimationIndex];
      const isLastAnimation =
        this.currentAnimationIndex === this.animationQueue.length - 1;


      // Play banner sound for this animation
      this.playWinPhaseSound(winType, "in");

      // Snap counter to previous segment's target — eliminates rAF/setTimeout drift
      const prevSegmentTarget = this.counterSegmentTargets[this.currentSegmentIndex - 1];
      if (prevSegmentTarget != null && this.counterAnimationRef) {
        this.counterAnimationRef.pause();
        this.counterAnimationRef = null;
        this.currentCounterValue = prevSegmentTarget;
        this.updateCounterDisplay(prevSegmentTarget);
      }

      // Detach counter from slot BEFORE reset — keeps counter visible at frozen scale
      // while spine IN animation plays (bone scale 0→1 won’t affect detached counter)
      // Detach counter from slot — force bone to rest scale (1.0) before reading
      // so we capture exact setup pose regardless of where in the IDLE pulse we are.
      if (!this.counterDetached) {
        const bone = this.winSpine.skeleton.findBone("value_placeholder");
        if (bone) {
          bone.scaleX = bone.data.scaleX;
          bone.scaleY = bone.data.scaleY;
          this.winSpine.skeleton.updateWorldTransform(2);
        }
        this.detachCounterFromSlot();
      }

      // Swap animation — don't clearTracks() to avoid setup pose flash
      // setAnimation() in playIntermediateAnimation/playFinalAnimation replaces the track
      this.winSpine.state.clearListeners();

      // Switch skin to the new tier
      this.setSkin(winType);

      const { inAnimation, idleAnimation } = this.findAnimations();

      if (isLastAnimation) {
        this.playFinalAnimation(inAnimation, idleAnimation, resolve, reject);
      } else {
        this.playIntermediateAnimation(
          inAnimation,
          idleAnimation,
          resolve,
          reject,
        );
      }
    });
  }

  /**
   * Start first animation in the sequence
   */
  startFirstAnimation() {
    return new Promise((resolve, reject) => {
      if (
        !this.winSpine ||
        !this.winSpine.state ||
        this.animationQueue.length === 0
      ) {
        resolve();
        return;
      }

      const winType = this.animationQueue[0]; // Start with first animation
      const isLastAnimation = this.animationQueue.length === 1;

      // Play main win sound once at animation start (based on final win type)
      this.playMainWinSound();

      // Play banner sound for first animation
      this.playWinPhaseSound(winType, "in");

      // Setup spine animation
      this.winSpine.state.clearTracks();
      this.winSpine.state.clearListeners();

      // Set skin for the first tier
      this.setSkin(winType);

      // Get animation names (shared: in, idle, out)
      const { inAnimation, idleAnimation } = this.findAnimations();

      // Calculate counter segments — one per visible tier
      // Counter only runs during IDLE phases (visible), not during IN transitions
      const isMaxWinFinal = this.finalWinType === "max_win" && !isLastAnimation;
      const counterTarget = isMaxWinFinal
        ? this.totalWinAmount - 1
        : this.totalWinAmount;
      const counterTiers = isMaxWinFinal
        ? this.animationQueue.length - 1
        : this.animationQueue.length;

      // Pre-compute per-tier counter targets using actual GDD thresholds
      // Each tier transition shows the exact threshold value (e.g., 40×bet for mega_win)
      this.counterSegmentTargets = [];
      for (let i = 0; i < counterTiers; i++) {
        if (i < counterTiers - 1) {
          // Intermediate segments: target = NEXT tier's threshold × bet
          // This means the counter hits exactly the threshold when the next tier appears
          const nextTierType = this.animationQueue[i + 1];
          const nextThreshold = WIN_TYPE_THRESHOLDS[nextTierType] || 0;
          const thresholdValue = Math.round(nextThreshold * this.betAmount);
          // Clamp to counterTarget in case threshold overshoots actual win
          this.counterSegmentTargets.push(Math.min(thresholdValue, counterTarget));
        } else {
          // Final segment: always target the full win amount
          this.counterSegmentTargets.push(counterTarget);
        }
      }
      this.currentSegmentIndex = 0;
      this.isMaxWinFinal = isMaxWinFinal;


      // First animation always plays IN → IDLE (spine scales counter in via bone)
      if (inAnimation && idleAnimation) {
        // Play in animation for its actual duration
        this.winSpine.state.setAnimation(0, inAnimation, false);

        // Start counter immediately — counts during IN + IDLE
        this.startTierCounter(isLastAnimation);

        // Get actual duration for in animation from spine data
        const inDuration = this.getSpineAnimationDuration("in");

        // After IN completes, start looping idle animation
        const inTimeout = new TickerTimeout(() => {
          if (this.isPlaying) {
            // Counter stays in slot — let it pulse with IDLE bone (1.0↔1.166)
            this.winSpine.state.addAnimation(0, idleAnimation, true, 0);
            this.playWinPhaseSound(this.animationQueue[this.currentAnimationIndex] || this.finalWinType, "idle");

            if (isLastAnimation) {
              // Single animation or last in sequence: wait for user click
              this.isWaitingForClick = true;
              this.currentAnimationPhase = "waiting-for-click";
              this.phaseStartTime = performance.now();
              this.phaseProgress = 0;
              resolve();
            }
          }
        }, inDuration);
        this.currentTimeouts.push(inTimeout);

        if (!isLastAnimation) {
          // Single timer for full tier duration — avoids nested TickerTimeout drift
          const tierTimeout = new TickerTimeout(() => {
            if (this.isPlaying) {
              this.currentAnimationIndex++;
              this.phaseStartTime = performance.now();
              this.phaseProgress = 0;
              this.playNextAnimation().then(resolve).catch(reject);
            }
          }, this.getTierDuration());
          this.currentTimeouts.push(tierTimeout);
        }
      } else if (idleAnimation) {
        // Fallback: just loop idle animation
        this.winSpine.state.setAnimation(0, idleAnimation, true);
        this.startTierCounter(isLastAnimation);
        if (isLastAnimation) {
          this.isWaitingForClick = true;
          this.currentAnimationPhase = "waiting-for-click";
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
        }
        resolve();
      } else {
        resolve();
      }
    });
  }

  /**
   * Get animation duration from spine data with detailed extraction
   */
  getSpineAnimationDuration(animationName) {
    if (
      !this.winSpine ||
      !this.winSpine.skeleton ||
      !this.winSpine.skeleton.data ||
      !this.winSpine.skeleton.data.animations
    ) {
      return 1000; // Default fallback
    }

    try {
      // Method 1: Try to get from skeleton data animations array
      const animation = this.winSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );

      if (animation && animation.duration) {
        const durationMs = Math.round(animation.duration * 1000);
        return durationMs;
      }

      // Method 2: Try to get from spine state data
      if (this.winSpine.state && this.winSpine.state.data) {
        const stateAnimation =
          this.winSpine.state.data.skeletonData.animations.find(
            (anim) => anim.name === animationName,
          );

        if (stateAnimation && stateAnimation.duration) {
          const durationMs = Math.round(stateAnimation.duration * 1000);
          return durationMs;
        }
      }

      // Method 3: Try to access the raw JSON data if available
      if (this.winSpine.skeleton.data.name && PIXI.Assets.cache) {
        // This might contain the original JSON data
        const skeletonAsset = Object.values(PIXI.Assets.cache._cache).find(
          (asset) =>
            asset && asset.animations && asset.animations[animationName],
        );

        if (skeletonAsset && skeletonAsset.animations[animationName]) {
          // Calculate duration from keyframes if available
          const animData = skeletonAsset.animations[animationName];
          let maxTime = 0;

          // Check all tracks for the longest duration
          Object.values(animData).forEach((track) => {
            if (Array.isArray(track)) {
              track.forEach((keyframe) => {
                if (keyframe.time && keyframe.time > maxTime) {
                  maxTime = keyframe.time;
                }
              });
            }
          });

          if (maxTime > 0) {
            const durationMs = Math.round(maxTime * 1000);
            return durationMs;
          }
        }
      }

      return 1000; // Default fallback
    } catch (error) {
      return 1000; // Default fallback
    }
  }

  /**
   * Get the total duration of one tier (IN + IDLE) from the spine data.
   * Extracted dynamically — works with any spine asset, no hardcoded values.
   *
   * @returns {number} Total tier duration in ms
   */
  getTierDuration() {
    const inDuration = this.getSpineAnimationDuration("in");
    const idleDuration = this.getSpineAnimationDuration("idle");
    return inDuration + idleDuration;
  }

  /**
   * Play intermediate animation — IN → IDLE, counter stays in slot.
   * Counter segment starts when IDLE begins (visible), not during IN (invisible).
   */
  playIntermediateAnimation(inAnimation, idleAnimation, resolve, reject) {
    const tierDuration = this.getTierDuration();

    // Start counter segment for this tier (counts during IN + IDLE)
    this.startTierCounter(false);

    if (inAnimation && idleAnimation) {
      // Play IN animation — spine stays visible, counter stays in slot
      this.winSpine.state.setAnimation(0, inAnimation, false);

      // Get actual IN duration from spine data
      const inDuration = this.getSpineAnimationDuration("in");

      // After IN completes, switch to IDLE loop
      const inTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          // Reattach counter to slot so it pulses with the IDLE bone animation
          // It will be detached again at the next playNextAnimation() call
          this.reattachCounterToSlot();

          this.winSpine.state.setAnimation(0, idleAnimation, true);
        }
      }, inDuration);
      this.currentTimeouts.push(inTimeout);

      // Single timer for full tier duration — avoids nested TickerTimeout drift
      const tierTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.currentAnimationIndex++;
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
          this.playNextAnimation().then(resolve).catch(reject);
        }
      }, tierDuration);
      this.currentTimeouts.push(tierTimeout);
    } else if (idleAnimation) {
      // Fallback: just idle
      this.winSpine.state.setAnimation(0, idleAnimation, true);

      const timeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.currentAnimationIndex++;
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
          this.playNextAnimation().then(resolve).catch(reject);
        }
      }, tierDuration);
      this.currentTimeouts.push(timeout);
    } else {
      this.currentAnimationIndex++;
      this.playNextAnimation().then(resolve).catch(reject);
    }
  }

  /**
   * Play final animation — IN → IDLE, wait for user click, OUT plays on close.
   * Counter segment starts when IDLE begins (visible).
   */
  playFinalAnimation(inAnimation, idleAnimation, resolve, reject) {
    const winType = this.animationQueue[this.currentAnimationIndex];

    // MAX WIN MUSIC SWITCH
    if (winType === "max_win") {
      try {
        soundManager.stop(SOUND_NAME.win_loop);
        const maxWinMusic = HowlCache[SOUND_NAME.max_win_loop];
        if (maxWinMusic) {
          maxWinMusic.loop(true);
          soundManager.fadeIn(SOUND_NAME.max_win_loop, 500);
        }
      } catch (error) {
        // Silent fail
      }
    }

    // MAX WIN: jump counter from totalWinAmount-1 to totalWinAmount
    if (winType === "max_win") {
      if (!this.counterJumpedToFinal) {
        this.jumpCounterToValue(this.totalWinAmount);
        this.counterJumpedToFinal = true;
        if (this.dispatch) {
          this.dispatch(setWinAnimationCounterFinished(true));
        }
        // GDD: "Tap to Continue should pop up after the win has been counted"
        this.showTapToContinue();
        this.startAutoCloseTimer();
      }
    }

    if (inAnimation && idleAnimation) {
      // Play IN animation — spine stays visible
      this.winSpine.state.setAnimation(0, inAnimation, false);

      // Start final counter segment (counts during IN + IDLE)
      if (winType !== "max_win") {
        this.startTierCounter(true);
      }

      // Get actual IN duration
      const inDuration = this.getSpineAnimationDuration("in");

      // After IN completes, loop IDLE and wait for user click
      const inTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          // Reattach counter to slot for the final IDLE — it pulses with the bone
          // and will naturally follow the OUT animation when closing
          this.reattachCounterToSlot();

          this.winSpine.state.addAnimation(0, idleAnimation, true, 0);
          this.playWinPhaseSound(this.finalWinType, "idle");

          this.isWaitingForClick = true;
          this.currentAnimationPhase = "waiting-for-click";
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
          resolve();
        }
      }, inDuration);

      this.currentTimeouts.push(inTimeout);
    } else if (idleAnimation) {
      // Reattach counter to slot so it pulses with the final IDLE
      this.reattachCounterToSlot();
      this.winSpine.state.setAnimation(0, idleAnimation, true);
      // Start final counter segment if no IN animation
      if (winType !== "max_win") {
        this.startTierCounter(true);
      }
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
      resolve();
    } else {
      this.finishAnimation();
      resolve();
    }
  }

  /**
   * Set spine skin for the given win tier.
   * Skins control which visual elements are shown (big_win, mega_win, etc.)
   * Animations are shared: in, idle, out.
   *
   * @param {string} winType - Win type matching a spine skin name (e.g. "big_win", "mega_win")
   */
  setSkin(winType) {
    if (!this.winSpine?.skeleton) return;

    try {
      const skinData = this.winSpine.skeleton.data.findSkin(winType);
      if (skinData) {
        this.winSpine.skeleton.setSkin(skinData);
        this.winSpine.skeleton.setSlotsToSetupPose();
        this.winSpine.skeleton.setToSetupPose();

        // setToSetupPose restores ALL slot attachments to defaults.
        // When counter is detached, clear the value/multiplier slots
        // so the baked-in placeholder doesn't appear during IN animations.
        if (this.counterDetached) {
          const valueSlot = this.winSpine.skeleton.findSlot("txt_slot_value");
          if (valueSlot) valueSlot.setAttachment(null);
          const multSlot = this.winSpine.skeleton.findSlot("txt_slot_multiplier");
          if (multSlot) multSlot.setAttachment(null);
        }
      }
    } catch (error) {
      // Silent fail — skin not found, keep current
    }
  }

  /**
   * Find animation names — in the secondary spine, animations are shared
   * across all tiers: "in", "idle", "out". Skin determines the visual.
   */
  findAnimations() {
    const animations = this.getAvailableAnimations();
    const inAnimation = animations.includes("in") ? "in" : null;
    const idleAnimation = animations.includes("idle") ? "idle" : null;
    const outAnimation = animations.includes("out") ? "out" : null;

    return { inAnimation, idleAnimation, outAnimation };
  }

  /**
   * Handle user click to skip counter or close animation
   *
   * Two-click logic:
   * 1. First click (during counter animation): Skips counter to final value
   * 2. Second click (after counter finished): Closes the entire animation
   *
   * Also handles spacebar input and prevents double-clicks with debouncing.
   *
   * @returns {boolean} True if click was handled, false if ignored
   */
  handleUserClick() {
    // Basic validation
    if (!this.isPlaying || !this.winSpine || !this.winSpine.state) {
      return false;
    }

    // Block interactions during closing or finishing phases
    if (this.isClosing || this.isFinishing) {
      return false;
    }

    // Skip protection: block input for first second of animation
    if (!this.canSkipAnimation) {
      return false;
    }

    // CRITICAL: If debounce timeout exists, block all clicks (prevents timeout buildup)
    // This ensures only ONE click is processed per 300ms window
    if (this.debounceTimeout) {
      return false;
    }

    // FIRST CLICK ONLY: Create debounce timeout to block subsequent clicks for 300ms
    // This timeout will be the ONLY timeout created, no matter how many clicks
    this.debounceTimeout = new TickerTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    // Check if counter is currently animating
    const isCounterAnimating = this.isCounterAnimating();
    const currentCounterValue = this.getCurrentCounterValue();
    const isCounterFinished = currentCounterValue >= this.totalWinAmount;

    // SIMPLE LOGIC: If counter is animating, skip it; if finished, close animation
    if (isCounterAnimating && !isCounterFinished) {
      // Counter is still animating - skip to final
      this.hasHandledSkipClick = true;

      // Jump counter to final value immediately
      this.jumpCounterToValue(this.totalWinAmount);

      this.counterJumpedToFinal = true;

      // Counter skipped to final - signal WinDisplay to show win text
      if (this.dispatch) {
        this.dispatch(setWinAnimationCounterFinished(true));
      }

      // Skip to final animation (handles multiplier/CTA and auto-close internally)
      this.skipToFinalAnimation();

      return true;
    } else {
      // Counter is finished or not animating - close animation
      this.hasHandledCloseClick = true;

      // Clear auto-close timer since user clicked manually
      if (this.autoCloseTimeout) {
        this.autoCloseTimeout.clear();
        this.autoCloseTimeout = null;
      }

      this.playClosingAnimation();

      return true;
    }
  }

  /**
   * Start auto-close timer for animations
   *
   * Automatically closes win animations if user doesn't interact after counter
   * reaches final value. Prevents animations from staying open indefinitely.
   * Timer is cleared if user clicks manually.
   */
  startAutoCloseTimer() {
    // Clear any existing auto-close timer
    if (this.autoCloseTimeout) {
      this.autoCloseTimeout.clear();
    }

    // Use MAX_WIN auto-close delay for max_win (30s), normal delay for other wins (3s)
    const autoCloseDelay =
      this.finalWinType === "max_win"
        ? FEATURES_CONFIG.MAX_WIN.AUTO_CLOSE_DELAY
        : FEATURES_CONFIG.WIN_CELEBRATION.AUTO_CLOSE_DELAY;

    // Start auto-close timer using configured delay
    this.autoCloseTimeout = new TickerTimeout(() => {
      if (this.isPlaying && !this.hasHandledCloseClick) {
        this.hasHandledCloseClick = true;
        this.playClosingAnimation();
      }
    }, autoCloseDelay);
  }

  /**
   * Skip all intermediate animations and jump straight to final animation
   *
   * When user clicks skip during counter animation:
   * - Stops all current animations and timeouts
   * - Jumps counter to final value instantly
   * - Plays final win type animation (EPIC for highest wins)
   * - If already playing final animation, handles smooth transition
   *
   * Used for progressive wins to skip BIG→SUPER→MEGA and go straight to EPIC.
   */
  skipToFinalAnimation() {
    const finalWinType = this.animationQueue[this.animationQueue.length - 1];
    const isAlreadyAtFinalAnimation =
      this.currentAnimationIndex === this.animationQueue.length - 1;

    // If we're already playing the final animation, handle it smoothly
    if (isAlreadyAtFinalAnimation && this.isPlaying) {
      this.handleSkipDuringFinalAnimation(finalWinType);
      return;
    }

    // Clear all timeouts and animations to stop intermediate animations
    this.clearAllResources();

    // Stop current banner sounds only (keep win_music playing - it plays throughout entire sequence)
    this.stopBannerSounds();

    // MAX WIN MUSIC SWITCH: When skipping to max_win, switch from win_music to max_win_music
    if (finalWinType === "max_win") {
      try {
        soundManager.stop(SOUND_NAME.win_loop);
        const maxWinMusic = HowlCache[SOUND_NAME.max_win_loop];
        if (maxWinMusic) {
          maxWinMusic.loop(true);
          soundManager.fadeIn(SOUND_NAME.max_win_loop, 500);
        }
      } catch (error) {
        // Silent fail
      }
    }

    // Jump to final animation index
    this.currentAnimationIndex = this.animationQueue.length - 1;

    // Jump counter to final value immediately when skipping (only if not already jumped)
    if (!this.counterJumpedToFinal) {
      this.jumpCounterToValue(this.totalWinAmount);
      this.counterJumpedToFinal = true;
      // Signal WinDisplay to show win text
      if (this.dispatch) {
        this.dispatch(setWinAnimationCounterFinished(true));
      }
    }

    // GDD §17.2: Always show multiplier/CTA when reaching final animation
    // (regardless of whether handleUserClick or skipToFinalAnimation jumped the counter)
    if (finalWinType === "max_win") {
      this.showTapToContinue();
    } else {
      this.showMultiplier();
      this.startAutoCloseTimer();
    }

    // Show spine if we're coming from counter-only phase
    if (!this.isVisible) {
      this.winSpine.alpha = 1; // Show (use alpha not visible)
      this.isVisible = true;
    }

    // Play banner sound for the final win type when skipping
    this.playWinPhaseSound(finalWinType, "in");

    // Detach counter from slot so it stays visible during skip transition
    this.detachCounterFromSlot();

    // Swap animation — don't clearTracks() as it flashes the setup pose for one frame
    // setAnimation() below replaces the current animation, mix durations handle the blend
    this.winSpine.state.clearListeners();

    // Switch skin to the final tier
    this.setSkin(finalWinType);

    const { inAnimation, idleAnimation } = this.findAnimations();

    if (inAnimation && idleAnimation) {
      this.winSpine.state.setAnimation(0, inAnimation, false);

      // After IN completes, loop IDLE
      const inDuration = this.getSpineAnimationDuration("in");

      const inTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          // Reattach counter to slot so it pulses with the final IDLE
          this.reattachCounterToSlot();

          this.winSpine.state.addAnimation(0, idleAnimation, true, 0);
          this.playWinPhaseSound(this.finalWinType, "idle");
          this.isWaitingForClick = true;
          this.currentAnimationPhase = "waiting-for-click";
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
        }
      }, inDuration);
      this.currentTimeouts.push(inTimeout);
    } else if (idleAnimation) {
      // Reattach counter to slot so it pulses with the final IDLE
      this.reattachCounterToSlot();
      this.winSpine.state.setAnimation(0, idleAnimation, true);
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      // No IN animation, show immediately
      // GDD §17.2: CTA button only on Max Win, multiplier only for BW/MW/SMW
      if (finalWinType === "max_win") {
        this.showTapToContinue();
      } else {
        this.showMultiplier();
        this.startAutoCloseTimer();
      }
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
    }
  }

  /**
   * Handle skip during final animation
   * Checks current phase and transitions smoothly to idle/out
   */
  handleSkipDuringFinalAnimation(finalWinType) {
    const { idleAnimation } = this.findAnimations();

    // Get current animation being played
    const currentAnimation = this.winSpine.state.tracks[0]?.animation?.name;

    // Check what phase we're currently in (generic names — skin determines visual)
    const inAnimationName = "in";
    const idleAnimationName = "idle";

    // GDD §17.2: Counter was jumped by handleUserClick — show multiplier now
    if (finalWinType === "max_win") {
      this.showTapToContinue();
    } else {
      this.showMultiplier();
    }
    this.startAutoCloseTimer();

    if (currentAnimation === inAnimationName) {
      // We're in the IN phase - smoothly transition to IDLE
      this.winSpine.state.clearListeners();

      if (idleAnimation) {
        // Reattach counter to slot so it pulses with the final IDLE
        this.reattachCounterToSlot();

        // Use addAnimation for smooth blend from IN to IDLE
        this.winSpine.state.addAnimation(0, idleAnimation, true, 0);

        // Set waiting state
        this.isWaitingForClick = true;
        this.currentAnimationPhase = "waiting-for-click";
        this.phaseStartTime = performance.now();
        this.phaseProgress = 0;
      }
    } else if (currentAnimation === idleAnimationName) {
      // We're already in IDLE phase - stay and wait for user
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
    } else {
      // Fallback: assume we're in IDLE and stay there
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
    }
  }

  /**
   * Play closing out animation when user clicks to continue
   */
  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.winSpine?.state) return;

    this.isClosing = true;
    this.isWaitingForClick = false;


    // Crossfade: fade out win loop music + idle phase sound, then play out sound
    try {
      soundManager.fadeOut(SOUND_NAME.win_loop, 500);
      soundManager.fadeOut(SOUND_NAME.max_win_loop, 500);

      // Fade out the idle phase sound so it blends with the out sound
      if (this.finalWinType) {
        const idleSoundKey = `${this.finalWinType}_idle`;
        if (SOUND_NAME[idleSoundKey]) {
          soundManager.fadeOut(SOUND_NAME[idleSoundKey], 500);
        }
      }
    } catch (error) {
      // Failed to handle music transition
    }

    // Counter is already reattached to slot (done when final IDLE started)
    // If still detached for any reason (e.g. skip), reattach now as safety net
    this.reattachCounterToSlot();

    const { outAnimation } = this.findAnimations();
    const outAnim = outAnimation || "out";

    this.winSpine.state.clearListeners();

    if (outAnim) {
      // Use setAnimation — the mix duration configured in initializeWinSpine
      // (stateData.setMix) handles the crossfade automatically
      this.winSpine.state.setAnimation(0, outAnim, false);
      this.playWinPhaseSound(this.finalWinType, "out");
      this.winSpine.state.addListener({
        complete: (e) => {
          if (e.animation.name === outAnim && this.isPlaying) {
            this.finishAnimation();
          }
        },
      });
    } else {
      this.finishAnimation();
    }
  }

  /**
   * Complete the animation sequence and perform full cleanup
   *
   * Called when user closes animation or auto-close timer expires.
   * Performs final cleanup: stops sounds, hides UI, clears resources,
   * dispatches Redux state changes, and resets all animation state.
   * This is the final method called when win celebration ends.
   */
  finishAnimation() {
    if (!this.winSpine || !this.initialized) {
      return;
    }

    // Set finishing flag immediately to prevent retriggering
    this.isFinishing = true;



    // Dispatch Redux state change immediately before any async operations
    if (this.dispatch) {
      this.dispatch(setWinAnimationsPlaying(false));
      this.dispatch(setWinAnimationCounterFinished(false));
      // Unblock symbol popup when win animations finish
      this.dispatch(setSymbolPopupBlocked(false));

      // Skip balance update when this is an H&W trigger round — the coin animations
      // are about to play and a balance update would cause jitter.
      // SceneTransitionOverlay already handles balance when returning to base game.
      const featureState = store.getState()?.game?.featureState;
      const isHoldAndWinTrigger =
        featureState?.type === "holdAndWin" && featureState?.phase === "intro";

      // Skip balance update during free spins — balance is synced once at the end of the feature
      const isFreeSpinActive = store.getState()?.featureGame?.freeSpinActive;

      // Skip balance update during free rounds — balance doesn't change mid-FR
      const isFreeRoundsActive = store.getState()?.freeRounds?.status === "active";

      // Provider-specific balance update strategy:
      // PopiPlay: Call refreshGameData() to sync with server (server tracks all transactions)
      // Redstone: Call updateDisplayedBalance() to sync displayed balance with Redux balance (already correct from spin)
      if (!isHoldAndWinTrigger && !isFreeSpinActive && !isFreeRoundsActive) {
        const replayState = store.getState()?.replay;
        if (replayState?.sessionActive) {
          // Replay (both entry points): never touch the live wallet
          // (syncGameState hits the real server — which the standalone
          // replay link doesn't even have, window.__OPTIONS__.api is empty
          // there — and updateDisplayedBalance reads the real Redux
          // balance). Add this step's win to the replay-only display
          // balance instead, at the exact same "win just finished" moment
          // the live path would sync. replaySlice.fetchReplayInit primes
          // replayBalance at the round's pre-win amount on every
          // playthrough, so this recreates the original reveal each time.
          this.dispatch(applyReplayWin(this.totalWinAmount || 0));
        } else if (handleShouldSyncBalanceOnWinClose()) {
          this.dispatch(syncGameState({ syncBalance: true }));
        } else {
          this.dispatch(updateDisplayedBalance());
        }
      }
    }

    // Stop win_music and max_win_music when animation finishes (safety stop — should already be stopped in playClosingAnimation)
    try {
      if (SOUND_NAME.win_loop) {
        soundManager.stop(SOUND_NAME.win_loop);
      }
      if (SOUND_NAME.max_win_loop) {
        soundManager.stop(SOUND_NAME.max_win_loop);
      }
    } catch (error) {
      // Silent fail
    }

    // Resume background music if it was playing before
    this.resumeBackgroundMusic();

    // Clear all resources to prevent buildup
    this.clearAllResources();

    // Reset playing states
    this.isPlaying = false;
    this.isWaitingForClick = false;

    // Disable scaling calculations when animation finishes
    this.shouldBeActive = false;

    // Reset phase tracking
    this.currentAnimationPhase = null;
    this.phaseStartTime = 0;
    this.phaseProgress = 0;

    // Counter and button don't need manual hiding — they're children of spine
    // and the spine's alpha change will automatically hide them



    // Hide spine immediately when finishAnimation starts to prevent visibility issues
    if (this.winSpine) {
      this.winSpine.alpha = 0.001; // Hide (use alpha not visible)
    }
    this.isVisible = false;

    // Fade out background with callback to clean up state after animation completes
    this.overlayRef.current?.fadeOutBackground(() => {
      // Stop banner sounds when overlay fades out completely
      this.stopBannerSounds();

      // Double-check spine is still hidden (defensive programming)
      if (this.winSpine) {
        this.winSpine.alpha = 0.001; // Hide (use alpha not visible)
      }
      this.isVisible = false;
    });

    // Reset animation queue and stored values
    this.animationQueue = [];
    this.currentAnimationIndex = 0;
    this.finalWinType = null;
    this.totalWinAmount = null;
    this.betAmount = null;
    this.lastWinAmount = null; // Reset to allow new win animations
    this.counterJumpedToFinal = false;
    this.counterDetached = false;

    // Reset user interaction guards
    this.hasHandledSkipClick = false;
    this.hasHandledCloseClick = false;
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;
    this.isClosing = false; // Reset closing state

    // Reset spine timeScale in case it was paused
    if (this.winSpine && this.winSpine.state) {
      this.winSpine.state.timeScale = 1;
    }

    // Reset auto-close timeout
    this.autoCloseTimeout = null;

    // Reset finishing flag after all cleanup is complete
    this.isFinishing = false;
  }

  /**
   * Immediately stop all win animations and perform emergency cleanup
   *
   * Used when component unmounts or critical errors occur.
   * Unlike finishAnimation(), this doesn't dispatch Redux state changes
   * or perform full UI transitions - just stops everything immediately.
   */
  stopWinAnimation() {


    // Clear all resources (timeouts + animations)
    this.clearAllResources();

    // Stop all win sounds
    this.stopAllWinSounds();

    // Resume background music if it was playing before
    this.resumeBackgroundMusic();

    if (this.winSpine && this.isPlaying) {
      this.winSpine.state?.clearListeners();
      this.winSpine.state?.clearTracks();
      this.winSpine.alpha = 0.001; // Hide (use alpha not visible)
      this.isPlaying = false;
      this.isVisible = false;

      // Disable scaling calculations when animation is stopped
      this.shouldBeActive = false;



      this.overlayRef.current?.fadeOutBackground();
    }

    // Reset user interaction guards
    this.hasHandledSkipClick = false;
    this.hasHandledCloseClick = false;
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;
    this.isClosing = false; // Reset closing state

    // Reset phase tracking
    this.currentAnimationPhase = null;
    this.phaseStartTime = 0;
    this.phaseProgress = 0;

    // Reset finishing flag if animation was stopped
    this.isFinishing = false;
  }

  /**
   * Cleanup resources
   */
  cleanup() {


    // Clear all resources before cleanup
    this.clearAllResources();

    // Cleanup counter
    if (this.counterContainer) {
      this.counterContainer.destroy({ children: true });
      this.counterContainer = null;
      this._charTexts = [];
    }

    // Cleanup multiplier
    if (this.multiplierContainer) {
      this.multiplierContainer.destroy({ children: true });
      this.multiplierContainer = null;
      this.multiplierText = null;
    }

    // Cleanup button
    if (this.buttonContainer) {
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
      this.buttonText = null;
    }

    // Disable scaling calculations when animation is no longer active
    this.shouldBeActive = false;

    if (this.winSpine) {
      // Defensive cleanup - ensure spine state is properly cleared
      if (this.winSpine.state) {
        try {
          this.winSpine.state.clearListeners();
          this.winSpine.state.clearTracks();
          this.winSpine.state.timeScale = 1.0;
        } catch (error) {
          // Spine might already be destroyed - continue cleanup
        }
      }

      // Remove from container safely
      if (this.container && this.container.children.includes(this.winSpine)) {
        this.container.removeChild(this.winSpine);
      }

      // Destroy spine instance
      try {
        this.winSpine.destroy();
      } catch (error) {
        // Spine might already be destroyed - continue cleanup
      }

      this.winSpine = null;
    }

    this.initialized = false;
  }
}

export default WinAnimationSecondaryController;
